import { HelpCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BLOCK_HELP } from "@/lib/carePlanBlockHelp";

/**
 * BlockHelpButton — Little "?" button next to a Care Plan block title.
 *
 * Clicking it opens a popover with contextual guidance for the specific
 * block (Quando preencher, Como, Exemplo) sourced from the shared
 * ``carePlanBlockHelp`` module. Uses the same content as the standalone
 * guide at /help/care-plan-guide so the gestor never has to context-switch.
 *
 * Placed inside the block header (see CarePlanBlocksEditor). Uses
 * ``e.stopPropagation()`` on the trigger click so clicking the ? doesn't
 * accidentally toggle the block's expand/collapse.
 */
export default function BlockHelpButton({ blockKey }) {
  const help = BLOCK_HELP[blockKey];
  if (!help) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          data-testid={`cp-block-help-${blockKey}`}
          aria-label={`Ajuda sobre o bloco ${blockKey}`}
          className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-muted transition-colors shrink-0"
        >
          <HelpCircle className="w-3.5 h-3.5" strokeWidth={1.6} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="start"
        sideOffset={12}
        className="w-80 p-4 bg-card border border-border shadow-lg rounded-lg"
        onClick={(e) => e.stopPropagation()}
        data-testid={`cp-block-help-content-${blockKey}`}
      >
        <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground font-semibold font-heading mb-3">
          Ajuda contextual · {blockKey}
        </div>
        <dl className="space-y-3 text-[13px] leading-relaxed">
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Quando preencher</dt>
            <dd className="text-foreground">{help.quando}</dd>
          </div>
          <div>
            <dt className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold mb-1">Como</dt>
            <dd className="text-foreground">{help.como}</dd>
          </div>
          <div className="pt-2 border-t border-border/60">
            <dt className="text-[11px] uppercase tracking-wider text-[hsl(var(--success))] font-semibold mb-1">Exemplo</dt>
            <dd className="text-foreground italic">"{help.exemplo}"</dd>
          </div>
        </dl>
        <div className="mt-4 pt-3 border-t border-border/60">
          <Link
            to="/help/care-plan-guide"
            data-testid={`cp-block-help-full-guide-${blockKey}`}
            className="text-[12px] text-primary hover:underline font-medium"
          >
            Abrir guião completo →
          </Link>
        </div>
      </PopoverContent>
    </Popover>
  );
}
